import React from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import { FONT, R, clamp, labelSx } from "./roomTheme";

/*
 * Lot entries for the lot lists (desktop side list, mobile rail).
 * The pages decide the image, the result label and the sold price; these only lay them out.
 */

const RESULT_COLOR = { SOLD: R.success, UNSOLD: R.danger, PASS: R.pass, RESERVE_NOT_MET: "#8A6D00" };
const RESULT_TEXT = { SOLD: "Sold", UNSOLD: "Unsold", PASS: "Pass", RESERVE_NOT_MET: "Reserve Not Met" };

/** Small result tag (Sold / Unsold / Pass / Reserve Not Met); nothing for other statuses. */
export function ResultTag({ status, sx }) {
  const text = RESULT_TEXT[status];
  if (!text) return null;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 0.75,
        py: 0.1,
        bgcolor: RESULT_COLOR[status],
        color: "#fff",
        fontFamily: FONT,
        fontSize: "0.6rem",
        fontWeight: 700,
        letterSpacing: "1px",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        ...sx,
      }}
    >
      {text}
    </Box>
  );
}

function Thumb({ src, size }) {
  return (
    <Box sx={{ width: size, height: size, flexShrink: 0, bgcolor: "#fff", border: `1px solid ${R.line}` }}>
      <Box component="img" src={src} alt="" loading="lazy" sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
    </Box>
  );
}

/** Row in the desktop lot list. `primaryHtml` / `secondaryHtml` are catalogue HTML. */
export const LotListItem = React.forwardRef(function LotListItem(
  { image, primaryHtml, secondaryHtml, status, soldPrice, soldColor, current, onClick },
  ref
) {
  return (
    <Box component="li" ref={ref} sx={{ listStyle: "none" }}>
      <ButtonBase
        onClick={onClick}
        aria-current={current ? "true" : undefined}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1.25,
          px: 1.25,
          py: 1,
          textAlign: "left",
          borderBottom: `1px solid ${R.line}`,
          borderLeft: `3px solid ${current ? R.ink : "transparent"}`,
          bgcolor: current ? R.soft : "transparent",
          "&:hover": { bgcolor: R.soft },
        }}
      >
        <Thumb src={image} size={52} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography component="div" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "0.8125rem", color: R.ink, lineHeight: 1.25, ...clamp(2) }} dangerouslySetInnerHTML={{ __html: primaryHtml }} />
          {secondaryHtml && (
            <Typography component="div" sx={{ fontFamily: FONT, fontStyle: "italic", fontSize: "0.78rem", color: R.text, lineHeight: 1.25, mt: 0.25, ...clamp(1) }} dangerouslySetInnerHTML={{ __html: secondaryHtml }} />
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
            <ResultTag status={status} />
            {soldPrice && <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "0.78rem", color: soldColor }}>{soldPrice}</Typography>}
          </Box>
        </Box>
       {current && (
          <Typography
            sx={{
              ...labelSx,
              color: "#D00000",
              fontSize: "0.65rem",
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            LIVE
          </Typography>
        )}
      </ButtonBase>
    </Box>
  );
});

/** Card in the mobile "lots" rail. */
export const LotRailItem = React.forwardRef(function LotRailItem(
  { image, lotNumber, title, artist, status, soldPrice, soldColor, current, onClick },
  ref
) {
  return (
    <Box ref={ref} sx={{ flex: "0 0 auto", width: 116, scrollSnapAlign: "start" }}>
      <ButtonBase
        onClick={onClick}
        aria-current={current ? "true" : undefined}
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          textAlign: "left",
          bgcolor: R.paper,
          border: `1px solid ${current ? R.ink : R.line}`,
          outline: current ? `1px solid ${R.ink}` : "none",
        }}
      >
        <Box sx={{ position: "relative", height: 92, bgcolor: R.soft }}>
          <Box component="img" src={image} alt="" loading="lazy" sx={{ width: "100%", height: "100%", objectFit: "contain", p: 1, boxSizing: "border-box", display: "block" }} />
          <Box component="span" sx={{ position: "absolute", top: 0, left: 0, px: 0.75, bgcolor: current ? R.ink : "rgba(17,17,17,0.75)", color: "#fff", fontFamily: FONT, fontWeight: 700, fontSize: "0.8rem" }}>
            {lotNumber}
          </Box>
          <ResultTag status={status} sx={{ position: "absolute", bottom: 4, left: 4 }} />
        </Box>
        <Box sx={{ p: 0.75, minHeight: 56 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", color: R.ink, lineHeight: 1.25, ...clamp(1) }}>{artist}</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: "0.72rem", fontStyle: "italic", color: R.text, lineHeight: 1.25, ...clamp(2) }}>{title}</Typography>
          {soldPrice && <Typography sx={{ fontFamily: FONT, fontSize: "0.75rem", fontWeight: 700, color: soldColor, mt: 0.25 }}>{soldPrice}</Typography>}
        </Box>
      </ButtonBase>
    </Box>
  );
});
